import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    setIsSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setMessage({
        type: 'success',
        text: 'If an account exists for that email, a reset link has been sent.',
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.message || 'Unable to send reset email. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cashy-purple-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">cashy.so</h1>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">Reset password</h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {message && (
            <div
              className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'error'
                  ? 'bg-red-100 border border-red-600 text-red-800'
                  : 'bg-green-100 border border-green-600 text-green-800'
              }`}
            >
              <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
              <p className="font-medium">{message.text}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cashy-purple-500 text-gray-900"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-cashy-purple-500 hover:bg-cashy-purple-600 disabled:bg-cashy-purple-400 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Mail size={20} />
                Send reset link
              </>
            )}
          </button>

          <p className="text-center text-gray-600">
            <Link to="/login" className="text-cashy-purple-500 hover:text-cashy-purple-600">
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};
